import {useViewportFitHeight} from '@github-ui/docked-side-panel/useViewportFitHeight'
import {ArtifactsPanel, useEmbeddedAvailable} from '@github-ui/scribe-artifacts/ArtifactPanel'
import {useArtifactsPanelActions, useArtifactsPanelState} from '@github-ui/scribe-artifacts/ArtifactPanelContext'
import {PortalContext, registerPortalRoot, SplitPageLayout} from '@primer/react'
import {clsx} from 'clsx'
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'

import {TEST_IDS} from '../../constants/test-ids'
import {type ActivityCommentJump, ActivityPanelProvider} from '../timeline/ActivityPanelContext'
import styles from './ArtifactsPanelLayout.module.css'

const SIDEBAR_WIDTH = {
  min: '450px',
  default: '544px',
  max: '768px',
} as const

// Portals resolve their destination by name, so descendants of the child issue view would
// otherwise mount in the global portal root and stay exposed while that view is hidden and
// inert. The name is a module constant rather than a `useId` because this subtree is server
// rendered: adding an ID here shifts every descendant's generated ID and breaks hydration.
// A fixed name means one registry entry, which is safe while only one child issue panel can
// be open at a time; nested child panels would need a per-instance name.
const CHILD_ISSUE_PORTAL_CONTAINER = 'issue-viewer-child-issue'
const CHILD_ISSUE_PORTAL_CONTEXT = {portalContainerName: CHILD_ISSUE_PORTAL_CONTAINER}

function registerChildIssuePortalRoot(node: HTMLDivElement | null) {
  if (node) registerPortalRoot(node, CHILD_ISSUE_PORTAL_CONTAINER)
}

function usePageFooter(enabled: boolean) {
  const subscribe = useCallback(
    (notify: () => void) => {
      if (!enabled) return () => {}
      const observer = new MutationObserver(notify)
      observer.observe(document.body, {childList: true, subtree: true})
      return () => observer.disconnect()
    },
    [enabled],
  )
  const getSnapshot = useCallback(
    () => (enabled ? document.querySelector<HTMLElement>('footer.footer') : null),
    [enabled],
  )
  return useSyncExternalStore(subscribe, getSnapshot, () => null)
}

function getScrollableAncestor(node: HTMLElement | null) {
  let current = node?.parentElement ?? null
  while (current) {
    const overflowY = getComputedStyle(current).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') return current
    current = current.parentElement
  }
  return null
}

function SidePanelIssueView({hidden, children}: {hidden: boolean; children: ReactNode}) {
  return (
    <div
      className={styles.SidePanelIssueView}
      data-testid={TEST_IDS.issueArtifactsChildIssueView}
      hidden={hidden}
      inert={hidden || undefined}
    >
      <div
        ref={registerChildIssuePortalRoot}
        className={styles.SidePanelIssuePortalRoot}
        data-testid={TEST_IDS.issueArtifactsChildPortalRoot}
      />
      <PortalContext value={CHILD_ISSUE_PORTAL_CONTEXT}>{children}</PortalContext>
    </div>
  )
}

export function ArtifactsPanelLayout({
  children,
  insideSidePanel = false,
}: {
  children: ReactNode
  insideSidePanel?: boolean
}) {
  const {activeArtifactId, editingArtifact} = useArtifactsPanelState()
  const {closePanel} = useArtifactsPanelActions()
  const embeddedAvailable = useEmbeddedAvailable()
  const panelOpen = activeArtifactId !== null
  const panelOpenDocked = panelOpen && embeddedAvailable && !insideSidePanel
  const [activityPanelOpen, setActivityPanelOpen] = useState(false)
  const [commentJump, setCommentJump] = useState<ActivityCommentJump>()
  const [activityPanelTarget, setActivityPanelTarget] = useState<HTMLElement | null>(null)
  const [hasOpenedDocked, setHasOpenedDocked] = useState(false)
  const panelContentRef = useRef<HTMLDivElement>(null)
  const activeDockedTabRef = useRef<HTMLButtonElement>(null)
  const activeSidePanelTabRef = useRef<HTMLButtonElement>(null)
  const sidePanelLayoutRef = useRef<HTMLDivElement>(null)
  const sidePanelArtifactViewRef = useRef<HTMLDivElement>(null)
  const sidePanelScrollTopRef = useRef(0)
  const previousSidePanelOpenRef = useRef(false)
  const previousEmbeddedAvailableRef = useRef(embeddedAvailable)
  const dockedPanelOpen = panelOpenDocked || activityPanelOpen
  const footer = usePageFooter(dockedPanelOpen && !insideSidePanel)
  const footerRef = useMemo(() => ({current: footer}), [footer])

  if (panelOpenDocked && activityPanelOpen) {
    setActivityPanelOpen(false)
  }

  if (panelOpenDocked && !hasOpenedDocked) {
    setHasOpenedDocked(true)
  }

  useEffect(() => {
    const wasEmbeddedAvailable = previousEmbeddedAvailableRef.current
    previousEmbeddedAvailableRef.current = embeddedAvailable
    if (wasEmbeddedAvailable || !panelOpenDocked) return

    activeDockedTabRef.current?.focus()
  }, [embeddedAvailable, panelOpenDocked])

  useViewportFitHeight(panelContentRef, {
    enabled: dockedPanelOpen,
    bottomBoundaryRef: footerRef,
    clampStickyAncestor: true,
  })

  const closeActivityPanel = useCallback(() => setActivityPanelOpen(false), [])
  const openActivityPanel = useCallback(
    (nextCommentJump?: ActivityCommentJump) => {
      closePanel()
      setCommentJump(nextCommentJump)
      setActivityPanelOpen(true)
    },
    [closePanel],
  )
  const activityPanelContextValue = useMemo(
    () => ({
      closePanel: closeActivityPanel,
      open: activityPanelOpen,
      openPanel: openActivityPanel,
      panelTarget: activityPanelTarget,
      commentJump,
    }),
    [activityPanelOpen, activityPanelTarget, commentJump, closeActivityPanel, openActivityPanel],
  )

  useEffect(() => {
    if (!insideSidePanel) return
    const sidePanel = getScrollableAncestor(sidePanelLayoutRef.current)
    const wasPanelOpen = previousSidePanelOpenRef.current

    if (panelOpen && !wasPanelOpen) {
      sidePanelScrollTopRef.current = sidePanel?.scrollTop ?? 0
      if (sidePanel) sidePanel.scrollTop = 0
      activeSidePanelTabRef.current?.focus()
    } else if (!panelOpen && wasPanelOpen && sidePanel) {
      sidePanel.scrollTop = sidePanelScrollTopRef.current
    }

    previousSidePanelOpenRef.current = panelOpen
  }, [insideSidePanel, panelOpen])

  const handleSidePanelArtifactKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- intercept before the containing SidePanel handles Escape
      if (event.key !== 'Escape' || event.defaultPrevented || editingArtifact) return
      const target = event.target
      if (target instanceof Element && target.closest('[role="menu"]')) return

      const hostDialog = sidePanelArtifactViewRef.current?.closest('[role="dialog"]')
      const nearestDialog = target instanceof Element ? target.closest('[role="dialog"]') : null
      if (nearestDialog && nearestDialog !== hostDialog) return

      event.preventDefault()
      event.stopPropagation()
      closePanel()
    },
    [closePanel, editingArtifact],
  )

  if (insideSidePanel) {
    return (
      <ActivityPanelProvider value={activityPanelContextValue}>
        <div
          ref={sidePanelLayoutRef}
          className={styles.SidePanelLayout}
          data-artifacts-child-panel-open={panelOpen || undefined}
          data-docked-panel-open={activityPanelOpen}
        >
          <SplitPageLayout>
            <SplitPageLayout.Content as="div" width="full" padding="none" className={styles.Content}>
              <SidePanelIssueView hidden={panelOpen}>{children}</SidePanelIssueView>
            </SplitPageLayout.Content>
            <SplitPageLayout.Sidebar
              resizable={activityPanelOpen}
              className={clsx(
                styles.SidePanel,
                styles.ChildIssueSidePanel,
                !activityPanelOpen && styles.HiddenSidePanel,
              )}
              divider="none"
              padding="none"
              responsiveVariant="fullscreen"
              sticky
              position="end"
              width={SIDEBAR_WIDTH}
            >
              <div ref={panelContentRef} className="height-full">
                <div ref={setActivityPanelTarget} className="height-full" hidden={!activityPanelOpen} />
              </div>
            </SplitPageLayout.Sidebar>
          </SplitPageLayout>
          <div
            ref={sidePanelArtifactViewRef}
            className={styles.SidePanelArtifactView}
            hidden={!panelOpen}
            onKeyDownCapture={handleSidePanelArtifactKeyDown}
          >
            <ArtifactsPanel mode="docked" activeTabRef={activeSidePanelTabRef} editable navigation="back" />
          </div>
        </div>
      </ActivityPanelProvider>
    )
  }

  return (
    <ActivityPanelProvider value={activityPanelContextValue}>
      <>
        <div
          className={styles.Layout}
          data-artifacts-panel-open={panelOpenDocked}
          data-docked-panel-open={dockedPanelOpen}
          data-testid={TEST_IDS.issueArtifactsPanelLayout}
        >
          <SplitPageLayout>
            <SplitPageLayout.Content as="div" width="full" padding="none" className={styles.Content}>
              <div className={styles.IssueContent}>{children}</div>
            </SplitPageLayout.Content>
            <SplitPageLayout.Sidebar
              resizable={dockedPanelOpen}
              className={clsx(styles.SidePanel, !dockedPanelOpen && styles.HiddenSidePanel)}
              divider="none"
              padding="none"
              responsiveVariant="fullscreen"
              sticky
              position="end"
              width={SIDEBAR_WIDTH}
            >
              <div ref={panelContentRef} className="height-full">
                {embeddedAvailable && hasOpenedDocked && (
                  <ArtifactsPanel mode="docked" activeTabRef={activeDockedTabRef} editable />
                )}
                <div ref={setActivityPanelTarget} className="height-full" hidden={!activityPanelOpen} />
              </div>
            </SplitPageLayout.Sidebar>
          </SplitPageLayout>
        </div>
        {!embeddedAvailable && <ArtifactsPanel mode="overlay" editable />}
      </>
    </ActivityPanelProvider>
  )
}
